import React, { useContext, useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Tooltip, IconButton } from "@mui/material";
import { InfoCircleOutlined } from "@ant-design/icons";
import "../FBOLandingFee.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ErrorPopup from "../../../components/element/ErrorPopup";
import Loader from "../../../components/element/Loader";
import NoDataPlaceholder from "../../../components/element/NoDataPlaceholder";
import SnackbarAlert from "../../../components/element/SnackbarAlert";
import RejectTripDialog from "../Views/RejectTripDialog";
import { StorageContext } from "../../../services/StorageProvider";
import { formatToTwoDecimal, getAirportData } from "../../../utils/utils";
import { DATE_FORMAT, TIME_FORMAT, TRIP_STATUS, REJECT_BUTTON_DISABLED_STATUSES, TRIP_STATUS_CODES } from "../../../utils/constants";
import useFBOApi from "../../../services/api/useFBOApi";
import { REJECTION_REASONS } from "../../../utils/constants";

dayjs.extend(utc);

const FBOLandingFeeTable = ({ landingFeeData, isLoading, error, onRefresh }) => {
  const { airportListData } = useContext(StorageContext);
  const [errorMessage, setErrorMessage] = useState("");
  const [snackbarKey, setSnackbarKey] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedTripData, setSelectedTripData] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [lastSuccessfulRejectId, setLastSuccessfulRejectId] = useState(null);
  const { rejectTrip } = useFBOApi();

  const rejectTripMutation = useMutation({
    mutationFn: ({ id, reason }) => rejectTrip(id, reason),
    onSuccess: (response, variables) => {
      setRejectDialogOpen(false);
      setSelectedTripData(null);
      setLastSuccessfulRejectId(variables.id);
      setShowSuccessMessage(true);
      if (onRefresh) {
        onRefresh();
      }
    },
    onError: (error) => {
      setErrorMessage(error.message);
      setSnackbarKey(prev => prev + 1);
    },
    retry: false,
  });

  // Clear error message and reset mutation state when data changes (pagination)
  useEffect(() => {
    setErrorMessage("");
    rejectTripMutation.reset();

    // Only clear success message if we're not on the same page with the rejected trip
    if (landingFeeData && lastSuccessfulRejectId) {
      const rejectedTripStillExists = landingFeeData.some(trip => trip.id === lastSuccessfulRejectId);
      if (!rejectedTripStillExists) {
        setShowSuccessMessage(false);
        setLastSuccessfulRejectId(null);
      }
    }
  }, [landingFeeData, lastSuccessfulRejectId]);

  if (error) {
    if (error.status === 404) {
      return <NoDataPlaceholder />;
    } else {
      return <ErrorPopup children={error.message}></ErrorPopup>;
    }
  }

  if (isLoading) {
    return <Loader />;
  }

  if (landingFeeData && landingFeeData.length === 0) {
    return <NoDataPlaceholder />;
  }

  const getTripStatus = (value, rejectionReason) => {
    if (value === TRIP_STATUS_CODES.COMPLETED) {
      return <div style={{ color: "#41D269" }}>{TRIP_STATUS.COMPLETED}</div>;
    } else if (value === TRIP_STATUS_CODES.CANCELLED) {
      return <div style={{ color: "#E8505B" }}>{TRIP_STATUS.CANCELLED}</div>;
    } else if (value === TRIP_STATUS_CODES.REJECTED) {
      const rejectionReasonText = REJECTION_REASONS[rejectionReason];
      return (
        <div style={{ color: "#E8505B" }}>
          {TRIP_STATUS.REJECTED}
          <Tooltip title={rejectionReasonText}
            arrow
            placement="bottom">
            <IconButton size="small" style={{ marginLeft: "4px" }}>
              <InfoCircleOutlined />
            </IconButton>
          </Tooltip>
        </div>
      );
    } else if (value === TRIP_STATUS_CODES.INVOICED) {
      return <div style={{ color: "#FFA500" }}>{TRIP_STATUS.INVOICED}</div>;
    } else if (value === TRIP_STATUS_CODES.PAID) {
      return <div style={{ color: "#41D269" }}>{TRIP_STATUS.PAID}</div>;
    } else if (value === TRIP_STATUS_CODES.PARTIALLY_PAID) {
      return <div style={{ color: "#FFA500" }}>{TRIP_STATUS.PARTIALLY_PAID}</div>;
    } else {
      return <div style={{ color: "#2978A0" }}>{TRIP_STATUS.UPCOMING}</div>;
    }
  };

  const handleReject = (item) => {
    setSelectedTripData(item);
    setRejectDialogOpen(true);
  };

  const handleCloseError = () => {
    setErrorMessage("");
  };

  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setSelectedTripData(null);
  };

  const handleRejectTrip = (tripData, reason) => {
    rejectTripMutation.mutate({ id: tripData.id, reason });
  };

  return (
    <div className="fbo-landing-fee-table-parent-container">
      <div className="fbo-landing-fee-table-container">
        <table className="fbo-landing-fee-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Trip ID</th>
              <th>Aircraft Reg. No.</th>
              <th>Aircraft Type</th>
              <th>MTOW (lbs)</th>
              <th>Owner</th>
              <th>Orig. Airport</th>
              <th>Dest. Airport</th>
              <th>Landing Date</th>
              <th>Landing Time</th>
              <th>Landing Fee</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {landingFeeData?.map((item, index) => (
              <React.Fragment key={index}>
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item?.tripTrackingId ?? '---'}</td>
                  <td>{item?.aircraftRegistrationNumber}</td>
                  <td>{item?.aircraftTypeIcaoCode}</td>
                  <td>{item?.maxTakeOffWeight}</td>
                  <td>{item?.aircraftOwnerName}</td>
                  <td className="airport-cell">
                    {
                      getAirportData(airportListData, item?.departureAirportId)
                        ?.name
                    }
                  </td>
                  <td className="airport-cell">
                    {
                      getAirportData(airportListData, item?.arrivalAirportId)
                        ?.name
                    }
                  </td>
                  <td>{dayjs(item?.arrivalTime).format(DATE_FORMAT)}</td>
                  <td>{dayjs(item?.arrivalTime).format(TIME_FORMAT)}</td>
                  <td>
                    {item?.gss !== TRIP_STATUS_CODES.CANCELLED
                      ? `BSD ${formatToTwoDecimal(item?.landingFeeCollected)}`
                      : "BSD 0.0"}
                  </td>
                  <td>{getTripStatus(item?.tripStatus, item?.tripRejectionReason)}</td>
                  <td>
                    <button
                      className="reject-button"
                      onClick={() => handleReject(item)}
                      disabled={REJECT_BUTTON_DISABLED_STATUSES.includes(item?.tripStatus)}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {errorMessage && (
        <SnackbarAlert
          key={snackbarKey}
          type="error"
          onClose={handleCloseError}
        >
          {errorMessage}
        </SnackbarAlert>
      )}
      {showSuccessMessage && (
        <SnackbarAlert
          key={`success-${snackbarKey}`}
          type="success"
          onClose={() => setShowSuccessMessage(false)}
        >
          Trip rejected successfully.
        </SnackbarAlert>
      )}
      <RejectTripDialog
        open={rejectDialogOpen}
        onClose={handleCloseRejectDialog}
        onReject={handleRejectTrip}
        tripData={selectedTripData}
        isLoading={rejectTripMutation.isPending}
      />
    </div>
  );
};

export default FBOLandingFeeTable;
